import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { LifeStageDateStatusEnum } from "@/Common/Models/LifeStageDateStatusEnum";
import { Locale } from "@/Common/Models/Localization/Locale";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { WeightUnit } from "@/Common/Utilities/Units/Units";
import { GestationalAgeComponent } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/GestationalAgeComponent";
import { ObstetricalFileControlRenderer } from "@/Patient/Components/MedicalNote/ObstetricalFile/ControlRenderer/ObstetricalFileControlRenderer";
import { ObstetricalHistoryItem } from "@/Patient/Models/ObstetricalFile/ObstetricalHistoryItem";
import { WeightUnitComponent } from "./Common/Subcomponent/WeightUnitComponent";

interface IObstetricalHistoryProps {
    readonly: boolean;
    updatePreviousPregnancies: (rows: ObstetricalHistoryItem[], triggerSave: boolean) => void;
    unit: WeightUnit;
    onMassUnitChange: (unit: WeightUnit, pregnancies: ObstetricalHistoryItem[]) => void;
    previousPregnanciesRows: ObstetricalHistoryItem[];
}

enum ObstetricalHistoryColumnTitle {
    Date = "Date",
    PlaceOfBirth = "PlaceOfBirth",
    GestationalAge = "GestationalAge",
    DurationOfLabor = "DurationOfLabor",
    BirthType = "BirthType",
    Comments = "Comments",
    Sex = "Sex",
    BirthWeight = "BirthWeight",
    BreastfedDuration = "BreastfedDuration",
    ChildPresentHealth = "ChildPresentHealth"
}

abstract class BaseObstetricalHistory extends BaseReactComponent<IObstetricalHistoryProps, { previousPregnanciesRows: ObstetricalHistoryItem[] }> {
    protected controlRenderer: ObstetricalFileControlRenderer;
    protected get locale(): Locale { return Locale.en; }
    protected abstract get columnsTitles(): { [index in ObstetricalHistoryColumnTitle]: React.ReactNode };
    protected abstract get columnMaxLength(): { [index in ObstetricalHistoryColumnTitle]: number };

    public constructor(props: IObstetricalHistoryProps) {
        super(props);
        this.state = { previousPregnanciesRows: [] };
    }

    public componentWillMount(): void {
        this.setControlRenderer(this.props.readonly);
        this.initState(this.props);
    }

    public componentWillReceiveProps(nextProps: IObstetricalHistoryProps): void {
        if (this.props.readonly !== nextProps.readonly)
            this.setControlRenderer(nextProps.readonly);
    }

    public initState(inputProps: IObstetricalHistoryProps): void {
        this.mutateState(s => {
            s.previousPregnanciesRows = inputProps.previousPregnanciesRows.map(p => new ObstetricalHistoryItem(p)).filter(p => p.hasValue()) ?? [];
            s.previousPregnanciesRows.push(new ObstetricalHistoryItem());
        });
    }

    private setControlRenderer(readonly: boolean): void {
        this.controlRenderer = new ObstetricalFileControlRenderer({
            readonly: readonly,
            onBlur: () => this.props.updatePreviousPregnancies(this.state.previousPregnanciesRows, true),
            locale: this.locale
        });
    }

    public render(): JSX.Element {
        return (
            <div className="row">
                <div className="col-md-12">
                    <table>
                        <thead>
                            <tr>
                                <th rowSpan={2} className="text-bold col-medium">{this.columnsTitles[ObstetricalHistoryColumnTitle.Date]}</th>
                                <th rowSpan={2} className="text-bold col-medium">{this.columnsTitles[ObstetricalHistoryColumnTitle.PlaceOfBirth]}</th>
                                <th rowSpan={2} className="text-bold col-medium">{this.columnsTitles[ObstetricalHistoryColumnTitle.GestationalAge]}</th>
                                <th rowSpan={2} className="text-bold">{this.columnsTitles[ObstetricalHistoryColumnTitle.DurationOfLabor]}</th>
                                <th rowSpan={2} className="text-bold col-medium">{this.columnsTitles[ObstetricalHistoryColumnTitle.BirthType]}</th>
                                <th rowSpan={2} className="text-bold">{this.columnsTitles[ObstetricalHistoryColumnTitle.Comments]}</th>
                                <th rowSpan={2} className="text-bold col-small">{this.columnsTitles[ObstetricalHistoryColumnTitle.Sex]}</th>
                                <th rowSpan={2} className="text-bold">{this.columnsTitles[ObstetricalHistoryColumnTitle.BirthWeight]}<br />{this.renderWeightUnit()}</th>
                                <th rowSpan={2} className="text-bold col-medium">{this.columnsTitles[ObstetricalHistoryColumnTitle.BreastfedDuration]}</th>
                                <th colSpan={2} className="text-bold col-medium">{this.columnsTitles[ObstetricalHistoryColumnTitle.ChildPresentHealth]}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {this.state.previousPregnanciesRows?.map(p => this.renderPreviousPregnancyRow(p))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    private renderWeightUnit(): JSX.Element {
        return (
            <WeightUnitComponent
                units={[WeightUnit.Gram, WeightUnit.Pound]}
                selectedUnit={this.props.unit}
                onUnitChange={unit => this.handleOnMassUnitChange(unit)}
                readonly={this.props.readonly}
                acceptableLocales={[Locale.en]}
                useAbbreviation
            />
        );
    }

    private handleOnMassUnitChange(unit: WeightUnit): void {
        if (this.props.unit === unit)
            return;

        this.props.onMassUnitChange(
            unit,
            [...this.state.previousPregnanciesRows.mutate(s => s.convertMass(this.props.unit, unit))]
        );
    }

    private renderPreviousPregnancyRow(obstetricalHistory: ObstetricalHistoryItem): JSX.Element {
        if (!obstetricalHistory)
            return null;

        return (
            <tr key={obstetricalHistory.getId()} className="max-height-none">
                <td>
                    <div>
                        {this.controlRenderer.renderDateWithPrecision({
                            date: obstetricalHistory.Date,
                            precision: obstetricalHistory.DatePrecision,
                            onChange: dateWithPrecision => this.mutateDate(obstetricalHistory.getId(), dateWithPrecision),
                            id: `ObstetricalHistorydate_${obstetricalHistory.getId()}`,
                            className: "table-date-selector",
                            isMonthPicker: true
                        })}
                    </div>
                </td>
                <td>
                    <div>

                        {this.controlRenderer.renderAutoCompleteTextbox({
                            value: obstetricalHistory.PlaceOfBirth,
                            autoCompleteItems: [
                                new SearchableItemModelT(this.getString("ObstetricalFile_PlannedBirthLocationOption_HospitalLabel"), "Hospital"),
                                new SearchableItemModelT(this.getString("Home2Label"), "Home"),
                                new SearchableItemModelT(this.getString("ObstetricalFile_PlannedBirthLocationOption_BirthCentreLabel"), "BirthCentre")
                            ],
                            onChange: value => this.mutateRowState(obstetricalHistory.getId(), p => p.PlaceOfBirth = value),
                            onAutoCompleteSelected: (code, model, value) => this.mutateRowState(obstetricalHistory.getId(), p => p.PlaceOfBirth = value),
                            maxLength: this.columnMaxLength[ObstetricalHistoryColumnTitle.PlaceOfBirth],
                            className: "table-textbox",
                            disableMatchAutoSorting: true
                        })}
                    </div>
                </td>
                <td>
                    <div>
                        <GestationalAgeComponent
                            id={`GestationWeeksAtBirth_${obstetricalHistory.getId()}`}
                            gestationalAge={obstetricalHistory.GestationWeeksAtBirth}
                            readonly={this.props.readonly}
                            onChange={value => this.mutateRowState(obstetricalHistory.getId(), p => p.GestationWeeksAtBirth = value)}
                            locale={Locale.en}
                            controlRenderer={this.controlRenderer}
                        />
                    </div>
                </td>
                <td>
                    <div>
                        {this.controlRenderer.renderTextbox({
                            value: obstetricalHistory.LabourHours,
                            onChange: value => this.mutateRowState(obstetricalHistory.getId(), p => p.LabourHours = value),
                            maxLength: this.columnMaxLength[ObstetricalHistoryColumnTitle.DurationOfLabor],
                            className: "table-textbox"
                        })}
                    </div>
                </td>
                <td>
                    <div>
                        {this.controlRenderer.renderAutoCompleteTextbox({
                            value: obstetricalHistory.TypeOfBirth,
                            autoCompleteItems:
                                [
                                    new SearchableItemModelT<string>(this.getString("SVDLabel"), "SVD"),
                                    new SearchableItemModelT<string>(this.getString("CSectionLabel"), "CSection"),
                                    new SearchableItemModelT<string>(this.getString("VacuumLabel"), "Vacuum"),
                                    new SearchableItemModelT<string>(this.getString("ForcepsLabel"), "Forceps"),
                                    new SearchableItemModelT<string>(this.getString("VaginalBirthAfterCesareanAbbrevLabel"), "VBAC"),
                                    new SearchableItemModelT<string>(this.getString("PosteriorVitreousDetachmentAbbrevLabel"), "PVD"),
                                    new SearchableItemModelT<string>(this.getString("TherapeuticAbortionAbbrevLabel"), "TAB"),
                                    new SearchableItemModelT<string>(this.getString("SpontaneousAbortionAbbrevLabel"), "SAB")
                                ],
                            onChange: value => this.mutateRowState(obstetricalHistory.getId(), p => p.TypeOfBirth = value),
                            onAutoCompleteSelected: (code, model, value) => this.mutateRowState(obstetricalHistory.getId(), p => p.TypeOfBirth = value),
                            maxLength: this.columnMaxLength[ObstetricalHistoryColumnTitle.BirthType],
                            className: "table-textbox",
                            disableMatchAutoSorting: true
                        })}
                    </div>
                </td>
                <td>
                    <div style={{ display: "block" }}>
                        {this.controlRenderer.renderResizableTextareaWithAutocomplete({
                            value: obstetricalHistory.Comments,
                            autoCompleteItems:
                                [
                                    new SearchableItemModelT<string>(this.getString("ObstetricalFile_NoComplicationsLabel"), "NoComplications"),
                                    new SearchableItemModelT<string>(this.getString("ObstetricalFile_SpontaneousAbortionLabel"), "SpontaneousAbortion"),
                                    new SearchableItemModelT<string>(this.getString("ObstetricalFile_TerminationLabel"), "Termination")
                                ],
                            onChange: value => this.mutateRowState(obstetricalHistory.getId(), p => p.Comments = value),
                            onAutoCompleteItemSelected: value => this.mutateRowState(obstetricalHistory.getId(), p => p.Comments = value),
                            maxLength: this.columnMaxLength[ObstetricalHistoryColumnTitle.Comments],
                            className: "table-textbox",
                            minHeight: "10px"
                        })}
                    </div>
                </td>
                <td>
                    <div>
                        {this.controlRenderer.renderAutoCompleteTextbox({
                            value: obstetricalHistory.Sex,
                            autoCompleteItems:
                                [
                                    new SearchableItemModelT<string>("M", "M"),
                                    new SearchableItemModelT<string>("F", "F")
                                ],
                            onChange: value => this.mutateRowState(obstetricalHistory.getId(), p => p.Sex = value),
                            onAutoCompleteSelected: (code, model, value) => this.mutateRowState(obstetricalHistory.getId(), p => p.Sex = value),
                            maxLength: this.columnMaxLength[ObstetricalHistoryColumnTitle.Sex],
                            className: "table-textbox",
                            disableMatchAutoSorting: true
                        })}
                    </div>
                </td>
                <td>
                    <div>
                        {this.controlRenderer.renderTextbox({
                            value: obstetricalHistory.getMassString(),
                            onChange: value => this.mutateRowState(obstetricalHistory.getId(), p => p.setMass(value ? Number(value) : null)),
                            maxLength: this.columnMaxLength[ObstetricalHistoryColumnTitle.BirthWeight],
                            isNumeric: true,
                            className: "table-textbox"
                        })}
                    </div>
                </td>
                <td>
                    <div>
                        {this.controlRenderer.renderTextbox({
                            value: obstetricalHistory.BreastfedMonths,
                            onChange: value => this.mutateRowState(obstetricalHistory.getId(), p => p.BreastfedMonths = value),
                            maxLength: this.columnMaxLength[ObstetricalHistoryColumnTitle.BreastfedDuration],
                            className: "table-textbox"
                        })}
                    </div>
                </td>
                <td>
                    <div>
                        {this.controlRenderer.renderResizableTextarea({
                            value: obstetricalHistory.ChildCurrentHealth,
                            onChange: value => this.mutateRowState(obstetricalHistory.getId(), p => p.ChildCurrentHealth = value),
                            className: "table-textbox",
                            minHeight: "34px",
                            maxLength: this.columnMaxLength[ObstetricalHistoryColumnTitle.ChildPresentHealth]
                        })}
                    </div>
                </td>
            </tr>
        );
    }

    private mutateDate(id: number, dateWithPrecision: DateTimeWithPrecision): void {
        if (dateWithPrecision.status === LifeStageDateStatusEnum.Valid || IsNullOrWhiteSpace(dateWithPrecision.text)) {
            this.mutateRowState(
                id,
                p => {
                    p.Date = LuxonDateTimeParser.parse(dateWithPrecision.value)?.toJSDate();
                    p.DatePrecision = dateWithPrecision.precision;
                },
                true
            );
        }
    }

    private mutateRowState(id: number, mutation: (state: ObstetricalHistoryItem) => void, triggerSave: boolean = false): void {
        let updateForm = false;
        this.mutateState(
            s => {
                let newRows = [...s.previousPregnanciesRows];
                newRows = newRows.mutate(
                    p => mutation(p),
                    p => p.getId() === id
                );

                let lastRowIndex = newRows.length - 1;

                for (let i = 0; i < lastRowIndex && newRows.any(); i++) {
                    if (newRows[i].hasValue())
                        continue;

                    updateForm = true;
                    newRows.splice(i, 1);
                    i--;
                    lastRowIndex--;
                }

                if (newRows[lastRowIndex].hasValue())
                    newRows.push(new ObstetricalHistoryItem());

                s.previousPregnanciesRows = newRows;
                return newRows;
            },
            () => this.props.updatePreviousPregnancies(this.state.previousPregnanciesRows, updateForm || triggerSave)
        );
    }
}

export { BaseObstetricalHistory, ObstetricalHistoryColumnTitle };
